import { useAuth } from "components/Auth";
import { useCallback, useEffect, useState } from "react";
import { MyProfilePresenter } from "./MyProfilePresenter";
import { useQuery, useQueryClient } from "react-query";
import {
  deleteProfileImage,
  getUserDetail,
  getUserProfile,
  putUpdateProfile,
  updateProfileIamge,
  updateTheme,
  putChangeProfile,
} from "api/userAPI";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  START_PAGE,
  UserDetail,
  PhoneVerify,
} from "api/interfaces/userInterface.interface";
import {
  getAccountTimeFormat,
  getAccountTimeFormatDate,
  getCurrentMilliSecond,
} from "utils/timeUtil";
import { Option, settingStartPageOptions } from "utils/options";
import {
  CustomError,
  LEVEL_TYPE,
  Validate,
  ValidateMsg,
} from "api/interfaces/commonInterface.interface";
import useApiError from "hook/useApiError";
import { isPhoneNumberValid } from "utils/validHelper";
import { setMode, ThemeText } from "redux/reducers/theme/themeReducer";
import useStatusMutation from "hook/useStatusMutation";
import { deletePasskey, postVerify } from "api/authAPI";
import useRegisterPasskey from "hook/useRegisterPasskey";
import { useFlag } from "@unleash/proxy-client-react";
import { checkEmpty, checkPasskeySupport, toTrim } from "utils/functions";

export enum UPDATE_TYPE {
  USER_ADDR,
  COMPANY_ADDR,
}

export default function MyProfileContainer(): JSX.Element {
  const theme = useAppSelector((state) => state.theme.theme);
  const { user, updateProfileImageUrl, updateUserInfo } = useAuth();
  const isPasskeyEnabled = useFlag("feature_login_passkey");
  const { handleErrorNotify, intlString, lang, notifySuccess, notifyError } =
    useApiError();
  const [passkeySupported, setPasskeySupported] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const [isModal, setIsModal] = useState<boolean>(false);
  const [modalType, setModalType] = useState<string>("");
  const [selectedPasskeyId, setSelectedPasskeyId] = useState<string>("");
  const [phoneVerify, setPhoneVerify] = useState<PhoneVerify>({
    isVerify: true,
    verifyCode: "",
    correctCode: "",
    originalPhoneNumber: "",
  });
  const [isCrop, setIsCrop] = useState<boolean>(false);
  const [isPhotoMenu, setIsPhotoMenu] = useState<boolean>(false);
  const profileImageUrl = user?.profileImg ? user.profileImg : "";
  const dispatch = useAppDispatch();

  // 원본 이미지
  const [originProfileImageUrl, setOriginProfileImageUrl] =
    useState<string>(profileImageUrl);

  const [isValidated, setIsValidated] = useState<Validate>({
    firstName: false,
    lastName: false,
    companyPhoneNumber: false,
  });
  const [isValidatedMsg, setIsValidatedMsg] = useState<ValidateMsg>({
    firstName: "",
    lastName: "",
    companyPhoneNumber: "",
  });

  const [originName, setOriginName] = useState<string>(user.name);
  const [profile, setProfile] = useState<UserDetail>({
    userId: "",
    firstName: "",
    lastName: "",
    email: "",
    address: "",
    phone: "",
    startPage: START_PAGE.MAP_VIEW,
    photo: "",
    newsLetter: false,
    companyName: "",
    companyAddress: "",
    companyPhoneNumber: "",
    department: "",
    jobTitle: "",
    notifyAlertMail: true,
    notifyReportMail: true,
    notifySms: true,
    notifyPush: true,
    use2faCode: "email",
    createAt: "",
    lastLoggedOn: "",
    provider: "",
    verifyPhone: null,
    passkeyList: [],
  });

  const [tempTheme, setTempTheme] = useState<ThemeText>(theme);

  const [startPageOptions, setStartPageOptions] = useState<Option[]>([
    ...settingStartPageOptions,
  ]);

  const onChangeisPhotoMenu = () => {
    setIsPhotoMenu(!isPhotoMenu);
  };
  const onChangeIsCrop = (type?: string) => {
    if (type === "close") {
      setIsCrop(false);
      return;
    }
    if (type === "open") {
      setIsCrop(true);
      return;
    }
    setIsCrop(!isCrop);
  };

  const onModal = (type: string) => {
    if (isModal) {
      refetch();
    }
    setIsModal(!isModal);
    setModalType(type);
  };
  const mutationUpdateProfileImage = useStatusMutation(updateProfileIamge, {
    onSuccess: (res: any) => {
      updateProfileImageUrl(res.result + "?v=" + getCurrentMilliSecond());
      onChangeIsCrop("close");
      refetch();
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.profile.notify.updateImage.fail);
    },
  });

  const mutationUpdateTheme = useStatusMutation(updateTheme, {
    onSuccess: (res: any) => {
      dispatch(setMode(tempTheme));
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.profile.notify.theme.fail);
    },
  });

  const mutationDeleteProfileImage = useStatusMutation(deleteProfileImage, {
    onSuccess: (res: any) => {
      // if (res.error === 0) {
      notifySuccess(lang.label.profile.notify.delImage.success);
      updateProfileImageUrl("");
      refetch();
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.profile.notify.delImage.fail);
    },
  });

  const onChangeTheme = (theme: "light" | "dark") => {
    setTempTheme(theme);
  };

  const [isDeleteProfileImageConfirm, setIsDeleteProfileImageConfirm] =
    useState<boolean>(false);

  const onDeleteProfileImageConfirm = useCallback(() => {
    setIsDeleteProfileImageConfirm(!isDeleteProfileImageConfirm);
  }, [isDeleteProfileImageConfirm]);

  const onDeleteProfileImage = useCallback(() => {
    if (!mutationDeleteProfileImage.idle) return;
    setIsDeleteProfileImageConfirm(!isDeleteProfileImageConfirm);
    if (profile.userId !== "") {
      mutationDeleteProfileImage.mutate({
        accountId: selectedAccount.accountId,
        userId: profile.userId,
      });
    }
  }, [
    isDeleteProfileImageConfirm,
    mutationDeleteProfileImage,
    profile.userId,
    selectedAccount.accountId,
  ]);

  const deleteErrorMsg = intlString(lang.PasskeyErrorCode[110008]);

  const mutationRemovePasskey = useStatusMutation(deletePasskey, {
    onSuccess: () => {
      notifySuccess(lang.PasskeyErrorCode[110007]);
      refetch();
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, deleteErrorMsg);
    },
    onSettled: () => {
      setSelectedPasskeyId("");
    },
  });

  const onClickRemovePasskey = (passkeyId: string) => {
    if (!passkeyId) return;
    if (!mutationRemovePasskey.idle) return;
    mutationRemovePasskey.mutate(passkeyId);
  };

  // profile image finish 버튼 클릭 시
  const onFinishProfileImage = useCallback(
    (croppedImage: File, originFile: File) => {
      if (user) {
        // 기존 프로필 이미지가 없고 신규 등록 or 변경된 이미지가 없을 경우에는 update 없이 modal 닫기
        // if (
        //   profileImageUrl === ""
        //   //  && newProfileImage === ""
        // ) {
        //   notify(
        //     "warning",
        //     intl.formatMessage({
        //       id: "label.profile.notify.photo.no",
        //       defaultMessage: "No profile image.",
        //     })
        //   );
        //   // onChangeIsCrop("close");
        //   return;
        // } else {
        let formData = new FormData();
        formData.append("file", croppedImage);
        formData.append("origin", originFile);

        if (!mutationUpdateProfileImage.idle) return;
        mutationUpdateProfileImage.mutate({
          accountId: selectedAccount.accountId,
          userId: user.userId,
          payload: formData,
        });
        // }
      } else {
        notifyError(lang.label.profile.notify.user.no);
        return;
      }
    },
    [mutationUpdateProfileImage, selectedAccount.accountId]
  );

  //회원 정보 조회시
  const { isLoading, refetch } = useQuery(
    ["profile", selectedAccount.accountId, user.userId],
    () =>
      getUserProfile({
        accountId: selectedAccount.accountId,
        userId: user.userId,
      }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      onSuccess: (res: any) => {
        if (res.error === 0 && res.result !== undefined) {
          res.result.lastLoggedOn =
            res.result.lastLoggedOn !== undefined
              ? getAccountTimeFormat(
                  res.result.lastLoggedOn!,
                  selectedAccount,
                  true
                )
              : "";
          res.result.createAt =
            res.result.createAt !== undefined
              ? getAccountTimeFormat(
                  res.result.createAt!,
                  selectedAccount,
                  true
                )
              : "";

          // start page option - user 가 속한 모든 account 확인
          let tempStartPageOpt = [...settingStartPageOptions];

          if (res.result.level !== undefined) {
            if (
              res.result.level.includes(LEVEL_TYPE.CP) ||
              res.result.level.includes(LEVEL_TYPE.DW)
            ) {
              tempStartPageOpt.push({
                value: "AccountList",
                label: "Organization List",
              });
            } else {
              tempStartPageOpt.push({
                value: "RecorderList",
                label: "Recorder List",
              });
            }
          }

          // console.log(res.result, "get profile");
          if (res.result.originalProfileImg) {
            setOriginProfileImageUrl(res.result.originalProfileImg);
          }
          setStartPageOptions(tempStartPageOpt);
          setProfile({
            ...res.result,
            passkeyList: (res.result.passkeyList || []).map((passkey: any) => ({
              ...passkey,
              createAt: getAccountTimeFormatDate(
                passkey.createAt,
                selectedAccount,
                true,
                true
              ),
            })),
          });
          setPhoneVerify((prev) => ({
            ...prev,
            isVerify: res.result.verifyPhone === false ? false : true,
            originalPhoneNumber: res.result.phone || "",
          }));
          setOriginName(res.result.name);
        } else {
          notifyError(lang.label.profile.notify.search.fail);
        }
      },
      onError: (res: any) => {
        notifyError(lang.label.profile.notify.search.fail);
      },
    }
  );
  const { onRegisterPasskey } = useRegisterPasskey({
    onSuccess: () => {
      refetch();
    },
    onFail: () => {
    },
  });

  const onConfirmAddPasskey = () => {
    onRegisterPasskey(profile.email);
  };

  const mutationUpdateProfile = useStatusMutation(putUpdateProfile, {
    onSuccess: (res: any) => {
      if (res.error === 0) {
        notifySuccess(lang.label.profile.notify.update.success);
        refetch();
      } else {
        notifyError(lang.label.profile.notify.update.fail);
      }
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.profile.notify.update.fail);
    },
  });

  const onChangeProfile = useCallback(
    (name: string, value: string | boolean) => {
      if (name === "phone") {
        setProfile((profile) => {
          return {
            ...profile,
            phone: value as string,
            use2faCode: value ? profile.use2faCode : "email",
            notifySms: value ? profile.notifySms : false,
          };
        });
        //폰넘버가 아예 없다면 전화번호 인증없이 삭제가능
        if (profile.phone === undefined || profile.phone === "") {
          setIsValidated((prev) => ({ ...prev, phone: false }));
          setIsValidatedMsg((prev) => ({ ...prev, phone: "" }));
          setPhoneVerify((prev) => ({ ...prev, isVerify: true }));
        }
      } else if (name === "use2faCode" && value === "phone") {
        setProfile((profile) => {
          return {
            ...profile,
            use2faCode: value,
            notifySms: true,
          };
        });
      } else {
        setProfile((profile) => {
          return { ...profile, [name]: value };
        });
      }
    },
    [profile]
  );

  const mutationChangePhoneNumber = useStatusMutation(putChangeProfile, {
    onSuccess: (res: any) => {
      if (res.error === 0) {
        mutationUpdateTheme.mutate({
          accountId: selectedAccount.accountId,
          userId: profile.userId,
          payload: {
            theme: tempTheme,
          },
        });
    
        const updateObject = Object.assign(
          {},
          {
            ...profile,
            firstName: toTrim(profile.firstName),
            lastName: toTrim(profile.lastName),
            address: toTrim(profile.address),
            companyAddress: toTrim(profile.companyAddress),
          }
        );
        delete updateObject.createAt;
        delete updateObject.lastLoggedOn;
        mutationUpdateProfile.mutate({
          accountId: selectedAccount.accountId,
          userId: user.userId,
          payload: updateObject,
        });
        return;
      }
      notifyError(lang.label.profile.notify.update.fail);
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.profile.notify.update.fail);
    },
  });

  const onChangeVerifyCode = useCallback((value: string) => {
    setPhoneVerify((prev) => ({ ...prev, verifyCode: value }));
  }, []);
  const mutationVerifyPhone = useStatusMutation(postVerify, {
    onSuccess: (res: any) => {
      if (res.error === 0) {
        setPhoneVerify((prev) => ({
          ...prev,
          correctCode: res.result.verifiedNumber,
        }));
      } else {
        setIsValidated((prev) => ({ ...prev, phone: true }));
        setPhoneVerify((prev) => ({ ...prev, correctCode: "" }));
        setIsValidatedMsg((prev) => ({
          ...prev,
          phone: intlString(lang.validateMsg.signup.requiredVerifyNumber),
        }));
        setProfile((prev) => ({ ...prev, verifyPhone: true }));
      }
    },
    onError: (err: CustomError) => {
      setIsValidated((prev) => ({ ...prev, phone: true }));
      setPhoneVerify((prev) => ({ ...prev, correctCode: "" }));
      handleErrorNotify(err, lang.validateMsg.signup.requiredVerifyNumber);
      setIsValidatedMsg((prev) => ({
        ...prev,
        phone: intlString(lang.validateMsg.signup.requiredVerifyNumber),
      }));
    },
  });

  const onRequestVerify = useCallback(
    (value: string) => {
      if (value === "cancel") {
        setPhoneVerify((prev) => ({
          ...prev,
          isVerify: true,
          correctCode: "",
          verifyCode: "",
        }));
        onChangeProfile(
          "phone",
          profile.verifyPhone === false ? "" : phoneVerify.originalPhoneNumber
        );
        return;
      }
      if (value === "phoneNumber") {
        if (phoneVerify.isVerify) {
          setPhoneVerify((prev) => ({
            ...prev,
            isVerify: false,
          }));
        }
        if (!checkEmpty(profile.phone) && !isPhoneNumberValid(profile.phone)) {
          setIsValidated((info) => {
            return { ...info, phone: true };
          });
          setIsValidatedMsg((msg) => {
            return {
              ...msg,
              phone: intlString(lang.validateMsg.signup.phone.invalidFormat),
            };
          });
          return;
        }
        if (!mutationVerifyPhone.idle) return;
        mutationVerifyPhone.mutate({
          payload: {
            phoneNumber: profile.phone,
          },
        });
        return;
      }
      if (value === "codeNumber") {
        if (phoneVerify.correctCode === phoneVerify.verifyCode) {
          setPhoneVerify((prev) => ({
            ...prev,
            isVerify: true,
            correctCode: "",
            verifyCode: "",
          }));
          setIsValidated((prev) => ({ ...prev, phone: false }));
          setIsValidatedMsg((prev) => ({ ...prev, phone: "" }));
          setProfile((prev) => ({
            ...prev,
            verifyPhone: true,
          }));
          return;
        }
        notifyError(lang.validateMsg.signup.verifyNumber);
        return;
      }
      if (value === "changePhoneNumber") {
        setPhoneVerify((prev) => ({ ...prev, isVerify: false }));
      }
    },
    [mutationVerifyPhone, phoneVerify.correctCode, phoneVerify.verifyCode]
  );

  const onClickSave = useCallback(() => {
    if (isLoading) return false;
    if (!mutationChangePhoneNumber.idle) return false;
    if (!mutationUpdateProfile.idle) return false;
    if (!mutationUpdateTheme.idle) return false;

    setIsValidated((info) => {
      return {
        ...info,
        firstName: false,
        lastName: false,
        companyPhoneNumber: false,
      };
    });
    setIsValidatedMsg((msg) => {
      return {
        ...msg,
        firstName: "",
        lastName: "",
        companyPhoneNumber: "",
      };
    });
    setProfile((profile) => {
      return {
        ...profile,
        firstName: toTrim(profile.firstName),
        lastName: toTrim(profile.lastName),
        address: toTrim(profile.address),
        companyAddress: toTrim(profile.companyAddress),
      };
    });

    if (checkEmpty(profile.firstName)) {
      setIsValidated((info) => {
        return { ...info, firstName: true };
      });
      setIsValidatedMsg((msg) => {
        return {
          ...msg,
          firstName: intlString(lang.validateMsg.signup.firstname),
        };
      });
      return;
    }
    if (checkEmpty(profile.lastName)) {
      setIsValidated((info) => {
        return { ...info, lastName: true };
      });
      setIsValidatedMsg((msg) => {
        return {
          ...msg,
          lastName: intlString(lang.validateMsg.signup.lastname),
        };
      });
      return;
    }

    if (profile.use2faCode === "phone" && !profile.phone) {
      notifyError(lang.validateMsg.signup.phone.cantBe2fa);
      return;
    }

    if (!phoneVerify.isVerify && profile.phone) {
      setIsValidated((info) => {
        return { ...info, phone: true };
      });
      setIsValidatedMsg((msg) => {
        return {
          ...msg,
          phone:
            phoneVerify.correctCode !== phoneVerify.verifyCode
              ? intlString(lang.validateMsg.profile.phone.verify.code)
              : intlString(lang.validateMsg.profile.phone.verify.fail),
        };
      });
      return;
    }
    if (
      !checkEmpty(profile.companyPhoneNumber) &&
      !isPhoneNumberValid(profile.companyPhoneNumber)
    ) {
      setIsValidated((info) => {
        return { ...info, companyPhoneNumber: true };
      });
      setIsValidatedMsg((msg) => {
        return {
          ...msg,
          companyPhoneNumber: intlString(
            lang.validateMsg.profile.phone.validate
          ),
        };
      });
      return;
    }
    if (
      profile.phone &&
      phoneVerify.originalPhoneNumber !== profile.phone &&
      phoneVerify.isVerify
    ) {
      mutationChangePhoneNumber.mutate({
        userId: profile.userId,
        type: "phoneNumber",
        payload: {
          phoneNumber: profile.phone,
        },
      });
      return;
    }
    if(tempTheme !== theme) {
      mutationUpdateTheme.mutate({
        accountId: selectedAccount.accountId,
        userId: profile.userId,
        payload: {
          theme: tempTheme,
        },
      });
    }

    const updateObject = Object.assign(
      {},
      {
        ...profile,
        firstName: toTrim(profile.firstName),
        lastName: toTrim(profile.lastName),
        address: toTrim(profile.address),
        companyAddress: toTrim(profile.companyAddress),
      }
    );
    delete updateObject.createAt;
    delete updateObject.lastLoggedOn;
    mutationUpdateProfile.mutate({
      accountId: selectedAccount.accountId,
      userId: user.userId,
      payload: updateObject,
    });
  }, [mutationUpdateProfile, profile, selectedAccount.accountId, user.userId]);
  const onChangeAddress = useCallback(
    (type: UPDATE_TYPE, value: string | boolean) => {
      if (type === UPDATE_TYPE.USER_ADDR) {
        setProfile((profile) => {
          return { ...profile, address: value as string };
        });
      } else if (type === UPDATE_TYPE.COMPANY_ADDR) {
        setProfile((profile) => {
          return { ...profile, companyAddress: value as string };
        });
      }
    },
    []
  );

  useEffect(() => {
    setTempTheme(theme);
  }, [theme]);

  useEffect(() => {
    checkPasskeySupport().then((isSupported) => {
      setPasskeySupported(isSupported);
    });
  }, []);
  return (
    <MyProfilePresenter
      isModal={isModal}
      modalType={modalType}
      onModal={onModal}
      originName={originName}
      profile={profile}
      isCrop={isCrop}
      isPhotoMenu={isPhotoMenu}
      onChangeIsCrop={onChangeIsCrop}
      profileImageUrl={profileImageUrl}
      // newProfileImageUrl={newProfileImageUrl}
      originProfileImageUrl={originProfileImageUrl}
      // onChangeProfileImage={onChangeProfileImage}
      onFinishProfileImage={onFinishProfileImage}
      onChangeisPhotoMenu={onChangeisPhotoMenu}
      onChangeProfile={onChangeProfile}
      onChangeAddress={onChangeAddress}
      accountLevel={selectedAccount.accountLevel}
      onClickSave={onClickSave}
      isValidated={isValidated}
      isValidatedMsg={isValidatedMsg}
      onDeleteProfileImage={onDeleteProfileImage}
      onDeleteProfileImageConfirm={onDeleteProfileImageConfirm}
      startPageOptions={startPageOptions}
      isDeleteProfileImageConfirm={isDeleteProfileImageConfirm}
      onChangeTheme={onChangeTheme}
      tempTheme={tempTheme}
      disabledSave={!mutationUpdateProfile.idle}
      phoneVerify={phoneVerify}
      onRequestVerify={onRequestVerify}
      onChangeVerifyCode={onChangeVerifyCode}
      onConfirmAddPasskey={onConfirmAddPasskey}
      onClickRemovePasskey={onClickRemovePasskey}
      setSelectedPasskeyId={setSelectedPasskeyId}
      selectedPasskeyId={selectedPasskeyId}
      isPasskeyEnabled={isPasskeyEnabled}
      passkeySupported={passkeySupported}
    />
  );
}
